import type { JSX } from "react";
import { useScenarioSpawnNotification } from "../../api/hooks/useScenarioSpawnNotification";

export type { SpawnedFromResponse } from "../../api/hooks/useScenarioSpawnNotification";

/**
 * scenario-spawn-mvp (Phase γ', Task 7.1) reader UI 通知 component。
 *
 * `GET /scenarios/:scenarioId/spawned-from` を呼び出し、 当 scenario 自身が他
 * scenario から派生したものであれば「あなたの物語から新 scenario が生まれました」
 * 通知 + 「読みに行く」 link (= source scenario detail へ) を表示する。
 *
 * design.md §Component 9 / Req 8 AC 4-6 対応:
 *  - props: scenarioId / scenarioTitle (= reader 表示用、 fetch には scenarioId 使用)
 *  - 初回 fetch (= 1 回のみ、 polling / SSE / WebSocket は future spec)
 *  - response shape `{ spawnedFromPlaylineId, spawnedFromScenarioId, sourceScenarioTitle }`
 *    全 field null (= origin scenario) なら component 自体を render しない (R8 AC 6)
 *  - spawnedFromScenarioId 非 null なら通知本文 + 「読みに行く」 button (= 本来 source
 *    scenario detail への navigation、 client-player には router 未導入のため anchor href)
 *
 * fetch は useScenarioSpawnNotification に委譲し、同一 scenario の複数 observer で
 * request/cache を共有する。 既存 design system component / className を再利用
 * (= 新規 design token 追加なし)。
 */

/**
 * BASE_URL は call 時に都度 resolve し、 module 読込時 const 評価を避ける。
 */
function resolveBaseUrl(): string {
  return (
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api/v1"
  );
}

export interface ScenarioSpawnNotificationProps {
  /** 通知対象の scenario id (= 当 scenario が派生かを fetch する key)。 */
  scenarioId: string;
  /** 表示用 scenario title (= 通知本文中の文脈表示には使わないが、 受領しておく)。
   *  task 7.1 で props として明示的に mandate されているため、 future copy 拡張
   *  (例「『xxx』から新 scenario が…」) のため reserve する。 */
  scenarioTitle: string;
}

export function ScenarioSpawnNotification({
  scenarioId,
  scenarioTitle: _scenarioTitle,
}: ScenarioSpawnNotificationProps): JSX.Element | null {
  const state = useScenarioSpawnNotification(resolveBaseUrl(), scenarioId);

  // 初期ロード中は何も表示しない (= flash 抑止、 sibling notification UX と整合)。
  if (state.isLoading) {
    return null;
  }

  // 取得失敗時は静かに非表示 (= 通知 component は best-effort、 reader 体験を妨げない)。
  if (state.isError || state.data === undefined) {
    return null;
  }

  // origin scenario (= 派生元なし) なら notification を出さない (R8 AC 6)。
  // 3 field 全 null は origin、 spawnedFromScenarioId が non-null のときのみ表示する。
  if (state.data.spawnedFromScenarioId === null) {
    return null;
  }

  const sourceScenarioId = state.data.spawnedFromScenarioId;
  const sourceTitle = state.data.sourceScenarioTitle;

  return (
    <div
      data-testid="scenario-spawn-notification"
      role="status"
      className="flex flex-col gap-2 p-4 bg-emerald-900/40 border border-emerald-700/50 rounded text-emerald-100"
    >
      <p className="text-sm font-semibold">
        あなたの物語から新 scenario が生まれました
      </p>
      {sourceTitle !== null && (
        <p
          data-testid="scenario-spawn-notification-source-title"
          className="text-xs text-emerald-200/80"
        >
          派生元: {sourceTitle}
        </p>
      )}
      <a
        data-testid="scenario-spawn-notification-link"
        href={`/scenarios/${encodeURIComponent(sourceScenarioId)}`}
        className="self-start px-3 py-1 text-sm bg-emerald-800 hover:bg-emerald-700 text-emerald-100 rounded border border-emerald-600"
      >
        読みに行く
      </a>
    </div>
  );
}
